import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Só entra em modo "soltar aqui" quando o que está sendo arrastado é arquivo do
 * sistema operacional. Arrastar texto selecionado ou uma bolha de mensagem
 * também dispara os eventos de drag, e sem essa checagem o overlay aparecia por
 * cima da conversa sem motivo.
 */
const carriesFiles = (event) => {
  const types = event?.dataTransfer?.types;
  if (!types) return false;
  return Array.from(types).includes('Files');
};

/**
 * Área de soltar arquivo reutilizável (chat de atendimento, chat do CRM, chat
 * interno). Devolve os handlers para espalhar no container e o estado do
 * overlay.
 *
 * @param {(files: File[]) => void} onFiles recebe os arquivos soltos
 * @param {boolean} disabled desliga o drop (ex.: ticket fechado)
 */
export default function useFileDropZone({ onFiles, disabled = false }) {
  const [isDragging, setIsDragging] = useState(false);

  // `dragenter`/`dragleave` disparam para cada elemento filho que o cursor
  // atravessa; sem contador de profundidade o overlay pisca ao passar por cima
  // das mensagens.
  const depthRef = useRef(0);

  const reset = useCallback(() => {
    depthRef.current = 0;
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (disabled) reset();
  }, [disabled, reset]);

  const handleDragEnter = useCallback(
    (event) => {
      if (disabled || !carriesFiles(event)) return;
      event.preventDefault();
      depthRef.current += 1;
      setIsDragging(true);
    },
    [disabled]
  );

  const handleDragOver = useCallback(
    (event) => {
      if (disabled || !carriesFiles(event)) return;
      // Sem preventDefault no dragover o navegador ignora o drop e abre o
      // arquivo na aba, derrubando a sessão aberta.
      event.preventDefault();
      event.dataTransfer.dropEffect = 'copy';
    },
    [disabled]
  );

  const handleDragLeave = useCallback(
    (event) => {
      if (disabled || !carriesFiles(event)) return;
      event.preventDefault();
      depthRef.current -= 1;
      if (depthRef.current <= 0) reset();
    },
    [disabled, reset]
  );

  const handleDrop = useCallback(
    (event) => {
      if (disabled || !carriesFiles(event)) return;
      event.preventDefault();
      event.stopPropagation();
      reset();
      const files = Array.from(event.dataTransfer.files || []);
      if (files.length > 0) onFiles(files);
    },
    [disabled, onFiles, reset]
  );

  return {
    isDragging,
    dropZoneProps: {
      onDragEnter: handleDragEnter,
      onDragOver: handleDragOver,
      onDragLeave: handleDragLeave,
      onDrop: handleDrop,
    },
  };
}
